/**
 * The recipient list: one model, whichever way the pairs were typed, pasted or
 * imported (PLAN §9).
 *
 * Every input mode produces `ParsedPair[]` and hands it to `buildRecipients`,
 * so validation, duplicate detection and the numbers on the confirmation screen
 * are the same code in every mode. A mode that validated its own input would
 * eventually validate it differently.
 *
 * Rows are never dropped and never repaired. An unusable row stays in the list
 * with a reason attached, because the administrator has to be able to see which
 * line of what they pasted was wrong — a list that silently shrank from 150 to
 * 148 is the worst possible outcome (§11.2, §59).
 *
 * Pure by construction (PLAN §4).
 */

import { normalizePhone, type PhoneProblem } from './phone.ts';
import { normalizePromoCode } from './template.ts';

/** Separator characters offered for paired lines (§7.1). Never auto-detected. */
export const SEPARATORS = {
  semicolon: ';',
  comma: ',',
  tab: '\t',
  pipe: '|',
} as const;

export type SeparatorName = keyof typeof SEPARATORS;

export const DEFAULT_SEPARATOR: SeparatorName = 'semicolon';

export interface Line {
  /** 1-based, and always the line in the text as pasted — the number the administrator can count to. */
  readonly number: number;
  readonly text: string;
}

/**
 * Splits pasted text into numbered, trimmed lines, keeping the blank ones:
 * Mode A skips them, Mode B splits on them, and neither can renumber.
 */
export function toLines(text: string): readonly Line[] {
  // \r\n from Windows and \r alone from older Mac exports.
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line, index) => ({ number: index + 1, text: line.trim() }));
}

export type PairProblem = 'missing-separator' | 'ambiguous-separator';

export interface ParsedPair {
  readonly line: number;
  readonly phone: string;
  readonly promoCode: string;
  /** Set when the line could not be split into a pair at all. */
  readonly problem?: PairProblem;
}

/**
 * Mode A: one `+996700123456;SUMMER25` per line, blank lines skipped (§7.1).
 *
 * A line the separator cannot split cleanly is flagged rather than guessed at.
 * Splitting on the first separator and hoping would quietly turn
 * `+996700123456;SUMMER;25` into a promo code of `SUMMER;25` for some inputs
 * and a mangled phone for others.
 */
export function parsePairedLines(
  text: string,
  separator: SeparatorName = DEFAULT_SEPARATOR,
): readonly ParsedPair[] {
  const character = SEPARATORS[separator];

  return toLines(text)
    .filter((line) => line.text !== '')
    .map((line) => {
      const fields = line.text.split(character);

      if (fields.length === 1) {
        return { line: line.number, phone: line.text, promoCode: '', problem: 'missing-separator' };
      }
      if (fields.length > 2) {
        return {
          line: line.number,
          phone: (fields[0] ?? '').trim(),
          promoCode: fields.slice(1).join(character).trim(),
          problem: 'ambiguous-separator',
        };
      }

      return {
        line: line.number,
        phone: (fields[0] ?? '').trim(),
        promoCode: (fields[1] ?? '').trim(),
      };
    });
}

/**
 * The separator Mode C's imported list travels under.
 *
 * A file is read in the browser and only the pairs are posted (PLAN §9), so the
 * list needs a text form that both sides agree on. Tab, because a promo code
 * containing one is close enough to impossible that the format is unambiguous
 * in practice — and where it is not, `parsePairedLines` flags the line as
 * ambiguous rather than mangling it, which is the same answer this program
 * gives everywhere else.
 */
export const IMPORT_SEPARATOR: SeparatorName = 'tab';

/**
 * The inverse of `parsePairedLines`: a pair list written back out as text.
 *
 * Only Mode C needs it — the browser has read a spreadsheet and has to hand the
 * result to the server through a form field. Round-tripping through the same
 * two functions means the server parses an import with exactly the code it
 * parses a paste with, rather than with a second reader that can disagree.
 *
 * Rows are renumbered by their position in the output, which is what the line
 * numbers of a combined list can honestly mean: append a second file and the
 * source line numbers of the two collide.
 */
export function formatPairedLines(
  pairs: readonly ParsedPair[],
  separator: SeparatorName = DEFAULT_SEPARATOR,
): string {
  const character = SEPARATORS[separator];
  return pairs.map((pair) => `${pair.phone}${character}${pair.promoCode}`).join('\n');
}

export type RecipientStatus = 'valid' | 'invalid' | 'duplicate';

/** Everything that can be wrong with one row, from any input mode. */
export type RecipientProblem = PhoneProblem | PairProblem | 'empty-code' | 'duplicate';

export interface Recipient {
  readonly line: number;
  /** Exactly as it was pasted, so the row can be shown back to whoever wrote it. */
  readonly rawPhone: string;
  /** `+996XXXXXXXXX`, or empty when the phone could not be normalised. */
  readonly phone: string;
  readonly promoCode: string;
  readonly status: RecipientStatus;
  readonly problem?: RecipientProblem;
  /** For a duplicate: the line that already claimed this number. */
  readonly duplicateOfLine?: number;
}

/** Which row of a repeated number is the one that gets sent (specification §16). */
export const DUPLICATE_RESOLUTIONS = ['first', 'last', 'manual'] as const;

export type DuplicateResolution = (typeof DUPLICATE_RESOLUTIONS)[number];

/**
 * First wins unless told otherwise: it is the row the administrator's eye finds
 * at the top of the list, and it is what every mode did before there was a
 * choice.
 */
export const DEFAULT_DUPLICATE_RESOLUTION: DuplicateResolution = 'first';

export interface BuildRecipientsOptions {
  readonly duplicates?: DuplicateResolution;
  /**
   * For `manual`: the lines to keep, one per repeated number. A group with none
   * of its lines listed falls back to the first — the screen reports it as
   * undecided and blocks Continue, but the preview still shows a coherent list
   * rather than a group with nothing chosen at all.
   */
  readonly keep?: Iterable<number>;
}

/**
 * Validates pairs and decides which row of each repeated number is sent.
 *
 * Duplicates are detected on the **normalised** phone, so `996700123456` and
 * `+996 700 123 456` collide as they should (PLAN §6). Which of them survives
 * is the administrator's call, because both answers are wrong sometimes: a list
 * pasted twice wants the first, a corrected row appended at the bottom wants
 * the last, and a list that is really two lists wants neither.
 *
 * Two passes rather than one, because with `last` and `manual` the winner is
 * not known until the group is complete.
 */
export function buildRecipients(
  pairs: readonly ParsedPair[],
  options: BuildRecipientsOptions = {},
): readonly Recipient[] {
  const resolution = options.duplicates ?? DEFAULT_DUPLICATE_RESOLUTION;
  const keep = new Set(options.keep ?? []);

  const draft = pairs.map((pair) => validateRow(pair));

  // Positions, not line numbers. A line number is what the administrator can
  // count to, and two pastes appended to each other can carry the same one
  // twice — which would make two different rows look like the same row.
  const claims = new Map<string, number[]>();
  draft.forEach((row, index) => {
    if (row.status !== 'valid') return;
    const seen = claims.get(row.phone);
    if (seen === undefined) claims.set(row.phone, [index]);
    else seen.push(index);
  });

  const winners = new Set<number>();
  const keptLineOf = new Map<string, number>();
  for (const [phone, positions] of claims) {
    const chosen = winner(draft, positions, resolution, keep);
    winners.add(chosen);
    keptLineOf.set(phone, (draft[chosen] as Recipient).line);
  }

  return draft.map((row, index) => {
    if (row.status !== 'valid' || winners.has(index)) return row;

    return {
      ...row,
      status: 'duplicate',
      problem: 'duplicate',
      duplicateOfLine: keptLineOf.get(row.phone) ?? row.line,
    };
  });
}

function winner(
  draft: readonly Recipient[],
  positions: readonly number[],
  resolution: DuplicateResolution,
  keep: ReadonlySet<number>,
): number {
  const first = positions[0] as number;

  if (resolution === 'last') return positions[positions.length - 1] as number;
  if (resolution === 'manual') {
    return positions.find((index) => keep.has((draft[index] as Recipient).line)) ?? first;
  }
  return first;
}

function validateRow(pair: ParsedPair): Recipient {
  const promoCode = normalizePromoCode(pair.promoCode);

  // The line could not be split at all: there is no pair here to validate.
  if (pair.problem !== undefined) return invalid(pair, promoCode, pair.problem);

  const parsed = normalizePhone(pair.phone);
  if (!parsed.ok) return invalid(pair, promoCode, parsed.problem);

  // Checked after the phone, so a row with two faults reports the phone — the
  // one that decides whether the row can be sent at all. The normalised number
  // is kept: it parsed, and the row is shown back with it.
  if (promoCode === '') return invalid(pair, promoCode, 'empty-code', parsed.phone);

  return {
    line: pair.line,
    rawPhone: pair.phone,
    phone: parsed.phone,
    promoCode,
    status: 'valid',
  };
}

export interface DuplicateGroup {
  /** Normalised, so the group is the number rather than the way it was written. */
  readonly phone: string;
  /** Every line claiming it, in the order they were pasted. */
  readonly lines: readonly number[];
  /** The one that will be sent. */
  readonly kept: number;
}

/**
 * The repeated numbers in a built list, for the panel that asks what to do
 * about them. Derived rather than returned by `buildRecipients`, because most
 * callers only need the rows.
 */
export function duplicateGroups(recipients: readonly Recipient[]): readonly DuplicateGroup[] {
  const lines = new Map<string, number[]>();
  const kept = new Map<string, number>();

  for (const row of recipients) {
    if (row.status === 'valid') {
      kept.set(row.phone, row.line);
    } else if (row.status !== 'duplicate') {
      continue;
    }
    const seen = lines.get(row.phone);
    if (seen === undefined) lines.set(row.phone, [row.line]);
    else seen.push(row.line);
  }

  const groups: DuplicateGroup[] = [];
  for (const [phone, claimed] of lines) {
    if (claimed.length < 2) continue;
    claimed.sort((a, b) => a - b);
    groups.push({ phone, lines: claimed, kept: kept.get(phone) ?? (claimed[0] as number) });
  }
  return groups;
}

/** The rows that would actually be sent. */
export function sendableRecipients(recipients: readonly Recipient[]): readonly Recipient[] {
  return recipients.filter((recipient) => recipient.status === 'valid');
}

/**
 * `phone` is empty unless the number itself normalised — a row refused for its
 * promo code still has a real number worth showing, while a row whose line
 * could not even be split has nothing that has been proved to be one.
 */
function invalid(
  pair: ParsedPair,
  promoCode: string,
  problem: RecipientProblem,
  phone = '',
): Recipient {
  return {
    line: pair.line,
    rawPhone: pair.phone,
    phone,
    promoCode,
    status: 'invalid',
    problem,
  };
}
